import { useEffect, useMemo, useState, useCallback } from "react";
import { db } from "~/db/db";

const usePrecoRegraV2 = ({ filters, setLoading, produto, filial, microrregiao, cluster, cidade, skip, take }) => {
  const [total, setTotal] = useState(0);
  const [totalRenderizado, setTotalRenderizado] = useState(0);
  const [precosRegraDexie, setPrecosRegraDexie] = useState([]);
  const [precoRegraFilters, setPrecoRegraFilters] = useState([]);
  const [precoRegra, setPrecoRegra] = useState(null);

  const applyFilters = useCallback((query, filterObj) => {
    if (!filterObj || !Object.keys(filterObj).some((key) => filterObj[key])) {
      return query;
    }

    return query.filter((precoRegra) => {
      const conditions = [
        !filterObj.grupoProduto || (precoRegra.grupoProdutoId ?? precoRegra.grupoMaterialId) === filterObj.grupoProduto,
        !filterObj.produto || precoRegra.produtoId === filterObj.produto,
        !filterObj.filial || precoRegra.filialId === filterObj.filial,
        !filterObj.cidade || precoRegra.cidadeId === filterObj.cidade,
        !filterObj.microrregiao || precoRegra.microrregiaoId === filterObj.microrregiao,
        !filterObj.grupoSegmento || precoRegra.grupoSegmentoId === filterObj.grupoSegmento,
        !filterObj.segmento || precoRegra.segmentoId === filterObj.segmento,
        !filterObj.cluster || precoRegra.clusterId === filterObj.cluster,
        !filterObj.unidadeMedida || precoRegra.unidadeMedidaId === filterObj.unidadeMedida,
      ];

      return conditions.every(Boolean);
    });
  }, []);

  const hasActiveFilters = useMemo(() => {
    return filters && Object.values(filters).some((value) => value);
  }, [filters]);

  const buildQuery = useCallback(
    (filterObj, order = true) => {
      let precos = db.tabelaPreco;

      if (order) {
        precos = precos.orderBy("nomeFilial");
      }

      return applyFilters(precos, filterObj);
    },
    [applyFilters]
  );

  const fetchPrecos = useCallback(async () => {
    setLoading?.(true);
    try {
      const precosQuery = buildQuery(filters);
      const filteredPrecos = precosQuery.filter((preco) => preco.ativo);

      const [totalCount, precos] = await Promise.all([
        filteredPrecos.count(),
        filteredPrecos
          .offset(0)
          .limit(skip + take)
          .toArray(),
      ]);

      setTotal(totalCount);
      setTotalRenderizado(precos.length);
      setPrecosRegraDexie(precos);
    } catch (error) {
      if (error) {
        setPrecosRegraDexie([]);
        setTotal(0);
        setTotalRenderizado(0);
      }
    } finally {
      setLoading?.(false);
    }
  }, [filters, skip, take, buildQuery, setLoading]);

  useEffect(() => {
    fetchPrecos();
  }, [fetchPrecos]);

  const precosRegra = useMemo(() => {
    if (!hasActiveFilters) {
      setLoading?.(false);
      return [];
    }

    const retorno = {};

    for (const obj of precosRegraDexie) {
      if (!retorno[obj.nomeFilial]) {
        retorno[obj.nomeFilial] = {
          header: {
            icon: "",
            title: obj.nomeFilial,
          },
          accordion: {
            enabled: false,
            label: obj.nomeFilial,
          },
          hideNegociacao: true,
          hidePrazo: true,
          showMicrorregiao: true,
          produtos: [],
        };
      }

      retorno[obj.nomeFilial].produtos.push({
        ...obj,
        color: {
          fob: "#FFF",
          cif: "#FFF",
        },
      });
    }

    setLoading?.(false);
    return Object.values(retorno);
  }, [precosRegraDexie, hasActiveFilters, setLoading]);

  const fetchFiltersPrecoRegra = useCallback(async () => {
    try {
      let precosQuery = db.tabelaPreco;

      if (microrregiao && cluster) {
        precosQuery = precosQuery.filter(
          (a) => a.microrregiaoId === microrregiao && (a.clusterId === cluster || !a.clusterId)
        );
      } else if (microrregiao) {
        precosQuery = precosQuery.filter((a) => a.microrregiaoId === microrregiao);
      } else {
        precosQuery = precosQuery.filter((a) => a.ativo === true);
      }

      const precos = await precosQuery.toArray();
      setPrecoRegraFilters(precos);
    } catch (error) {
      console.error("Error fetching filters:", error);
      setPrecoRegraFilters([]);
    }
  }, [microrregiao, cluster]);

  useEffect(() => {
    fetchFiltersPrecoRegra();
  }, [fetchFiltersPrecoRegra]);

  const extractUniqueIds = useCallback((items, field) => {
    if (!Array.isArray(items)) return [];
    return [...new Set(items.map((row) => row[field]).filter((id) => id !== undefined))];
  }, []);

  const filtrosDisponiveisPrecoRegra = useMemo(() => {
    if (!precoRegraFilters?.length) {
      return {};
    }

    const opcoesFiltradas = precoRegraFilters.filter((precoRegra) => {
      if (!filters) return true;
      return (
        (!filters.grupoProduto || precoRegra.grupoMaterialId === filters.grupoProduto) &&
        (!filters.produto || precoRegra.produtoId === filters.produto) &&
        (!filters.filial || precoRegra.filialId === filters.filial) &&
        (!filters.microrregiao || precoRegra.microrregiaoId === filters.microrregiao) &&
        (!filters.cidade || precoRegra.cidadeId === filters.cidade) &&
        (!filters.grupoSegmento || precoRegra.grupoSegmentoId === filters.grupoSegmento) &&
        (!filters.segmento || precoRegra.segmentoId === filters.segmento) &&
        (!filters.cluster || precoRegra.clusterId === filters.cluster) &&
        (!filters.unidadeMedida || precoRegra.unidadeMedidaId === filters.unidadeMedida)
      );
    });

    return {
      grupoMaterialDisponiveis: extractUniqueIds(opcoesFiltradas, "grupoMaterialId"),
      cidadesDisponiveis: extractUniqueIds(opcoesFiltradas, "cidadeId"),
      clustersDisponiveis: extractUniqueIds(opcoesFiltradas, "clusterId"),
      produtosDisponiveis: extractUniqueIds(opcoesFiltradas, "produtoId"),
      filiaisDisponiveis: extractUniqueIds(opcoesFiltradas, "filialId"),
      microrregioesDisponiveis: extractUniqueIds(precoRegraFilters, "microrregiaoId"),
      grupoSegmentoDisponiveis: extractUniqueIds(opcoesFiltradas, "grupoSegmentoId"),
      segmentosDisponiveis: extractUniqueIds(opcoesFiltradas, "segmentoId"),
      familiasDisponiveis: extractUniqueIds(opcoesFiltradas, "familiaId"),
      unidadesMedidaDisponiveis: extractUniqueIds(opcoesFiltradas, "unidadeMedidaId"),
    };
  }, [precoRegraFilters, filters, extractUniqueIds]);

  const fetchPrecoRegra = useCallback(async () => {
    if (!produto || !filial || !microrregiao) {
      setPrecoRegra(null);
      return;
    }

    try {
      const result = await db.tabelaPreco
        .where({ produtoId: produto, filialId: filial, microrregiaoId: microrregiao })
        .toArray();

      if (result.length > 0) {
        const preco =
          result.find((a) => a.clusterId === cluster) || result.find((a) => a.cidadeId === cidade) || result[0];
        setPrecoRegra(preco);
      } else {
        setPrecoRegra(null);
      }
    } catch (error) {
      if (error) {
        setPrecoRegra(null);
      }
    }
  }, [produto, filial, microrregiao, cluster, cidade]);

  useEffect(() => {
    fetchPrecoRegra();
  }, [fetchPrecoRegra]);

  return {
    precosRegra,
    precosRegraDexie,
    total,
    precoRegra,
    filtrosDisponiveisPrecoRegra,
    totalRenderizado,
  };
};

export default usePrecoRegraV2;
